import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../hooks/useUsers";
import { usePositions } from "../../hooks/usePositions";
import { useChannels } from "../../hooks/useChannels";
import { useTypes } from "../../hooks/useTypes";
import { titleOfType } from "../../utils/leadTypeCascade";
import { QUICK_STATUS_FILTER_LABELS } from "../../utils/searchFilterState";
import {
  CHANNEL_TYPES,
  isActiveChannel,
  isWhatsAppOfficialChannelTipo,
} from "../../constants/channelCatalog";
import {
  estimateOpsIntelligence,
  triggerOpsIntelligence,
  getOpsIntelligenceRuns,
  getOpsIntelligenceRun,
  getChannelTemplates,
  createTeamDm,
} from "../../api/client";
import { toTimeInputValue } from "../../utils/faqSkillConfig";
import {
  channelFormatHint,
  isFormatAllowedForChannel,
  pruneFormatsForChannel,
} from "../../utils/opsIntelligenceChannelFormats";
import {
  assessOpsIntelligenceWhatsappTemplate,
  buildOpsIntelligenceDeliveryPreview,
  expectedHeaderForFormat,
  HEADER_KIND_LABELS,
  isOpsIntelligenceWhatsappTemplateSelectable,
  OPS_WA_SAMPLE_BY_HEADER,
  sampleTemplateCopyForFormat,
  opsTemplateIssueCopy,
} from "../../utils/opsIntelligenceWhatsappTemplateCompat";
import { formatDatePtBR, formatDateTimePtBR } from "../../utils/dateTime";

/** Período civil → "DD/MM/AAAA–DD/MM/AAAA". */
function formatOpsPeriodRange(start, end, sep = "–") {
  const a = formatDatePtBR(start);
  const b = formatDatePtBR(end);
  if (!a || a === "—" || !b || b === "—") return "";
  return `${a}${sep}${b}`;
}

const OUTPUT_OPTIONS = [
  { key: "text", labelKey: "opsOutText", fallback: "Texto" },
  { key: "audio", labelKey: "opsOutAudio", fallback: "Áudio" },
  { key: "image_chart", labelKey: "opsOutChart", fallback: "Gráfico" },
  { key: "infographic", labelKey: "opsOutInfographic", fallback: "Infográfico" },
  { key: "video_narrated", labelKey: "opsOutVideoNarrated", fallback: "Vídeo explicativo" },
  { key: "video_animated", labelKey: "opsOutVideoAnimated", fallback: "Vídeo animado" },
];

const FORMAT_HEADER_HINT = {
  text: "Exige template com Header None ou Text e variável {{1}} (ideal {{1}} nome + {{2}} resumo).",
  audio: "Exige template com Header Document — a Meta/Gupshup não têm Header Áudio.",
  image_chart: "Exige template com Header Image (mesmo tipo do Infográfico).",
  infographic: "Exige template com Header Image (mesmo tipo do Gráfico).",
  video_narrated: "Exige template com Header Video.",
  video_animated: "Exige template com Header Video.",
};

const OPEN_QUEUE_DEFAULT = ["Pendente", "Em Atendimento"];
const PERIOD_MOVEMENT_DEFAULT = ["Concluída", "Perdida", "Contato Futuro"];
const MEDIA_FORMAT_KEYS = [
  "audio",
  "image_chart",
  "infographic",
  "video_narrated",
  "video_animated",
];

function primaryFormatOf(formats) {
  const list = Array.isArray(formats) ? formats : [];
  return list.find((f) => MEDIA_FORMAT_KEYS.includes(f)) || "text";
}

function readAnalysisScope(skillConfig = {}) {
  const raw = skillConfig.analysis_scope;
  const selected = Array.isArray(skillConfig.filters?.filters)
    ? skillConfig.filters.filters.map(String)
    : [];
  const openSet = new Set(OPEN_QUEUE_DEFAULT);
  if (!raw || typeof raw !== "object") {
    if (!selected.length) {
      return {
        open_queue: { enabled: true, statuses: [...OPEN_QUEUE_DEFAULT] },
        period_movement: { enabled: true, statuses: [...PERIOD_MOVEMENT_DEFAULT] },
      };
    }
    const open = selected.filter((s) => openSet.has(s));
    const movement = selected.filter((s) => !openSet.has(s));
    return {
      open_queue: {
        enabled: open.length > 0,
        statuses: open.length ? open : [...OPEN_QUEUE_DEFAULT],
      },
      period_movement: {
        enabled: movement.length > 0,
        statuses: movement.length ? movement : [...PERIOD_MOVEMENT_DEFAULT],
      },
    };
  }
  const openStatuses = Array.isArray(raw.open_queue?.statuses) && raw.open_queue.statuses.length
    ? raw.open_queue.statuses
    : [...OPEN_QUEUE_DEFAULT];
  const moveStatuses =
    Array.isArray(raw.period_movement?.statuses) && raw.period_movement.statuses.length
      ? raw.period_movement.statuses
      : [...PERIOD_MOVEMENT_DEFAULT];
  return {
    open_queue: {
      enabled: raw.open_queue ? raw.open_queue.enabled !== false : true,
      statuses: openStatuses,
    },
    period_movement: {
      enabled: raw.period_movement ? raw.period_movement.enabled !== false : true,
      statuses: moveStatuses,
    },
  };
}

function unionScopeStatuses(scope) {
  const labels = [];
  if (scope?.open_queue?.enabled) labels.push(...(scope.open_queue.statuses || []));
  if (scope?.period_movement?.enabled) labels.push(...(scope.period_movement.statuses || []));
  return [...new Set(labels.map(String))];
}

function deliveryStatusKey(delivery) {
  const dlrCode = Number(delivery?.channel?.dlr?.statusCode);
  if (delivery?.channel?.dlr?.failed || delivery?.deliveryStatus === "error") return "error";
  if (delivery?.deliveryStatus === "read" || dlrCode === 3) return "read";
  if (delivery?.deliveryStatus === "delivered" || dlrCode === 2) return "delivered";
  if (delivery?.deliveryStatus === "skipped") return "skipped";
  if (delivery?.deliveryStatus === "recorded") return "recorded";
  if (delivery?.deliveryStatus === "sent" || dlrCode === 1 || delivery?.channel?.ok) return "sent";
  if (delivery?.dm?.ok && !delivery?.channel?.ok && delivery?.channel?.skipped) return "recorded";
  return "pending";
}

const PERIOD_OPTIONS = [
  { key: "yesterday", labelKey: "opsPeriodYesterday" },
  { key: "last_24h", labelKey: "opsPeriodLast24h" },
  { key: "last_7d", labelKey: "opsPeriodLast7d" },
  { key: "last_30d", labelKey: "opsPeriodLast30d" },
  { key: "custom_relative", labelKey: "opsPeriodCustom" },
];

const SCHEDULE_UNIT_OPTIONS = [
  {
    value: "days",
    labelKey: "opsScheduleDays",
    fallback: "Dias",
    singularKey: "opsScheduleDay",
    singularFallback: "Dia",
  },
  {
    value: "weeks",
    labelKey: "opsScheduleWeeks",
    fallback: "Semanas",
    singularKey: "opsScheduleWeek",
    singularFallback: "Semana",
  },
  {
    value: "months",
    labelKey: "opsScheduleMonths",
    fallback: "Meses",
    singularKey: "opsScheduleMonth",
    singularFallback: "Mês",
  },
];

function scheduleUnitOptionLabel(option, count, copy) {
  const n = Number(count) === 1;
  return copy(
    n ? option.singularKey : option.labelKey,
    n ? option.singularFallback : option.fallback
  );
}

function scheduleSummaryText({ n, unit, time, t, copy }) {
  const count = Math.max(1, Number(n) || 1);
  const clock = time || "08:00";
  if (count === 1 && unit === "days") {
    return t("agentsListPage.opsScheduleSummaryDaily", {
      defaultValue: "Todos os dias às {{time}} (horário de Brasília).",
      time: clock,
    });
  }
  if (count === 1 && unit === "weeks") {
    return t("agentsListPage.opsScheduleSummaryWeekly", {
      defaultValue: "Toda semana às {{time}} (horário de Brasília).",
      time: clock,
    });
  }
  if (count === 1 && unit === "months") {
    return t("agentsListPage.opsScheduleSummaryMonthly", {
      defaultValue: "Todo mês às {{time}} (horário de Brasília).",
      time: clock,
    });
  }
  const opt = SCHEDULE_UNIT_OPTIONS.find((o) => o.value === unit) || SCHEDULE_UNIT_OPTIONS[0];
  return t("agentsListPage.opsScheduleSummaryEvery", {
    defaultValue: "A cada {{n}} {{unit}} às {{time}} (horário de Brasília).",
    n: count,
    unit: scheduleUnitOptionLabel(opt, count, copy).toLowerCase(),
    time: clock,
  });
}

function mergeFilters(base = {}, patch = {}) {
  return { ...(base || {}), ...(patch || {}) };
}

/** Normaliza resposta de GET /channels/:id/templates (vários envelopes). */
function normalizeChannelTemplatesPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.data?.templates)) return payload.data.templates;
  return [];
}

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = Number(n?.id);
    if (!Number.isFinite(id) || id <= 0) return flattenTypesTree(n?.children || []);
    // Só tipos ativos (status=1). Sem status no payload → mantém (API já filtra por padrão).
    const status = n.status;
    if (status != null && status !== "" && Number(status) !== 1) {
      return [];
    }
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : null;
    const nome = titleOfType(n) || String(id);
    return [
      { id, id_pai: Number.isFinite(idPai) && idPai > 0 ? idPai : null, nome },
      ...flattenTypesTree(n.children || []),
    ];
  });
}

function normalizeScheduleUnit(unit) {
  const u = String(unit || "").toLowerCase();
  if (u === "weeks" || u === "months" || u === "days") return u;
  // horas/minutos legados → dias
  return "days";
}

function formatInt(n, locale) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return Math.round(v).toLocaleString(locale);
}

function formatApprox(n, locale) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v < 10) return Math.round(v).toLocaleString(locale);
  return `~${Math.round(v).toLocaleString(locale)}`;
}

function ConfigAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

function PeopleOrPositionField({
  label,
  helperText,
  placeholder,
  options,
  userIds = [],
  cargoIds = [],
  onChange,
  disabled,
  loading,
  positionPrefix = "Cargo",
}) {
  const selected = useMemo(() => {
    const fromIds = (ids, type, fallbackName) =>
      (ids || []).map((id) => {
        const key = `${type}:${id}`;
        return (
          (options || []).find((o) => o.key === key) || {
            key,
            type,
            id: Number(id),
            name: fallbackName(id),
            group: type === "position" ? "Cargos" : "Pessoas",
          }
        );
      });
    return [
      ...fromIds(cargoIds, "position", (id) => `Cargo #${id}`),
      ...fromIds(userIds, "user", (id) => `Usuário #${id}`),
    ];
  }, [options, userIds, cargoIds]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      filterSelectedOptions
      groupBy={(o) => o.group}
      options={options || []}
      value={selected}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(o) => o.name || ""}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      onChange={(_, next) =>
        onChange({
          userIds: next.filter((o) => o.type === "user").map((o) => Number(o.id)).filter((n) => n > 0),
          cargoIds: next.filter((o) => o.type === "position").map((o) => Number(o.id)).filter((n) => n > 0),
        })
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.key}
            size="small"
            color={option.type === "position" ? "primary" : "default"}
            variant={option.type === "position" ? "filled" : "outlined"}
            label={
              option.type === "position"
                ? `${positionPrefix}: ${option.name}`
                : option.name
            }
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={selected.length ? undefined : placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function MultiSelectField({
  label,
  helperText,
  options,
  valueIds,
  onChangeIds,
  getOptionLabel,
  disabled,
  loading,
  placeholder,
}) {
  const selected = useMemo(() => {
    const idSet = new Set((valueIds || []).map((id) => Number(id)));
    return (options || []).filter((o) => idSet.has(Number(o.id)));
  }, [options, valueIds]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      filterSelectedOptions
      options={options || []}
      value={selected}
      loading={loading}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
      onChange={(_, next) => onChangeIds(next.map((o) => Number(o.id)).filter((n) => n > 0))}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            size="small"
            label={getOptionLabel(option)}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={selected.length ? undefined : placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function OpsTemplateFeedback({ template, t, expectedHeader = "none" }) {
  if (!template) return null;
  const assessment = assessOpsIntelligenceWhatsappTemplate(template, { expectedHeader });
  if (assessment.ok) return null;
  const issue = opsTemplateIssueCopy(t, assessment);
  if (!issue) return null;
  return (
    <Alert severity={issue.severity} sx={{ mt: 0.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {issue.title}
      </Typography>
      {issue.cause ? (
        <Typography variant="body2" sx={{ mb: 0.75 }}>
          {issue.cause}
        </Typography>
      ) : null}
      {issue.fix ? <Typography variant="body2">{issue.fix}</Typography> : null}
    </Alert>
  );
}

function templateDisplayName(tpl) {
  return tpl?.elementName || tpl?.nome || tpl?.name || (tpl?.id != null ? `#${tpl.id}` : "");
}

function templateMetaLine(tpl, copy) {
  const parts = [];
  if (tpl?.languageCode) parts.push(String(tpl.languageCode));
  const cat = String(tpl?.category || "").trim();
  if (cat) parts.push(cat);
  const header =
    HEADER_KIND_LABELS[tpl?.headerKind] ||
    HEADER_KIND_LABELS[String(tpl?.templateType || "").toLowerCase()] ||
    null;
  if (header) parts.push(header);
  const vars = Number(tpl?.bodyParamCount);
  if (Number.isFinite(vars) && vars > 0) {
    parts.push(
      copy("opsWaTemplateVars", "{{n}} variável(is)").replace("{{n}}", String(vars))
    );
  }
  return parts.join(" · ");
}

function emptyWhatsappTemplates() {
  return {
    text: null,
    audio: null,
    image_chart: null,
    infographic: null,
    video_narrated: null,
    video_animated: null,
  };
}

function readWhatsappTemplates(skillConfig) {
  const base = emptyWhatsappTemplates();
  const raw =
    skillConfig?.whatsapp_templates && typeof skillConfig.whatsapp_templates === "object"
      ? skillConfig.whatsapp_templates
      : {};
  for (const key of Object.keys(base)) {
    const n = Number(raw[key] || 0);
    base[key] = n > 0 ? n : null;
  }
  if (!base.text) {
    const legacy = Number(skillConfig?.whatsapp_template_id || 0);
    if (legacy > 0) base.text = legacy;
  }
  return base;
}

export default function OpsIntelligenceConfigSection({
  skillConfig = {},
  mergeOpsConfig,
  readOnly = false,
  currentAgentId,
}) {
  const { t, i18n } = useTranslation("settings");
  const navigate = useNavigate();
  const locale = i18n.language || "pt-BR";
  const copy = (key, fallback) =>
    t(`agentsListPage.${key}`, { defaultValue: fallback });
  const [openingTeamChat, setOpeningTeamChat] = useState(false);

  const { data: usersData, isLoading: usersLoading } = useUsers(1, 500, "", "todos");
  const { data: positionsResp, isLoading: positionsLoading } = usePositions(1, 1000, "");
  const { data: channelsQuery, isLoading: channelsLoading } = useChannels(1, 500, "");
  const { data: typesResp, isLoading: typesLoading } = useTypes(1, 1000, "");

  const users = usersData?.users ?? [];
  const positions = positionsResp?.positions || [];
  const channels = useMemo(
    () => (channelsQuery?.channels || []).filter((c) => isActiveChannel(c)),
    [channelsQuery?.channels]
  );
  const typesTree = typesResp?.types || [];

  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  /** Só true após fetch concluir para o canal atual — evita apagar template salvo na corrida inicial. */
  const [waTemplatesReady, setWaTemplatesReady] = useState(false);
  const waTemplatesLoadedForChannelRef = useRef(null);
  const [waGuideOpen, setWaGuideOpen] = useState(false);
  const [testRun, setTestRun] = useState(null);
  const [testProgress, setTestProgress] = useState("");
  const testAbortRef = useRef(0);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  const formats = Array.isArray(skillConfig.output_formats) ? skillConfig.output_formats : ["text"];
  const selectedFormat = primaryFormatOf(formats);
  const recipients = Array.isArray(skillConfig.recipients) ? skillConfig.recipients : [];
  const filters = skillConfig.filters || {};
  const analysisScope = useMemo(() => readAnalysisScope(skillConfig), [skillConfig]);
  const deliveryChannel = skillConfig.delivery_channel || "email";
  const waTemplatesByFormat = readWhatsappTemplates(skillConfig);

  const patchAnalysisScope = (nextScope) => {
    const next = {
      open_queue: nextScope.open_queue,
      period_movement: {
        ...nextScope.period_movement,
        date_field: nextScope.period_movement?.date_field || "atualizacao",
      },
    };
    mergeOpsConfig({
      analysis_scope: next,
      filters: mergeFilters(filters, { filters: unionScopeStatuses(next) }),
    });
  };

  const setWhatsappTemplateForFormat = (formatKey, templateId) => {
    const next = { ...waTemplatesByFormat, [formatKey]: templateId != null ? Number(templateId) : null };
    mergeOpsConfig({
      whatsapp_templates: next,
      whatsapp_template_id: next.text || null,
    });
  };

  const DELIVERY_CHANNEL_TIPOS = useMemo(
    () => ({
      email: [CHANNEL_TYPES.EMAIL_AWS_SES, CHANNEL_TYPES.SMTP, CHANNEL_TYPES.MAILGUN],
      whatsapp: [
        CHANNEL_TYPES.WHATSAPP_CLOUD,
        CHANNEL_TYPES.WHATSAPP_GUPSHUP,
        CHANNEL_TYPES.WHATSAPP_QR,
      ],
      sms: [CHANNEL_TYPES.SMS, CHANNEL_TYPES.TWILIO_SMS],
      voice: [CHANNEL_TYPES.TWILIO_PHONE],
    }),
    []
  );

  const sendAccountOptions = useMemo(() => {
    const tipos = new Set(DELIVERY_CHANNEL_TIPOS[deliveryChannel] || []);
    return (Array.isArray(channels) ? channels : []).filter(
      (c) => tipos.has(Number(c.tipo)) && isActiveChannel(c)
    );
  }, [channels, deliveryChannel, DELIVERY_CHANNEL_TIPOS]);

  const selectedSendAccount = useMemo(() => {
    const id = Number(skillConfig.channel_id || 0);
    return sendAccountOptions.find((c) => Number(c.id) === id) || null;
  }, [sendAccountOptions, skillConfig.channel_id]);

  const needsWhatsappTemplate =
    deliveryChannel === "whatsapp" &&
    selectedSendAccount &&
    isWhatsAppOfficialChannelTipo(selectedSendAccount.tipo);

  const mediaFormatsSelected = formats.some((f) =>
    ["audio", "image_chart", "infographic", "video_narrated", "video_animated"].includes(f)
  );

  const typesFlat = useMemo(() => flattenTypesTree(typesTree), [typesTree]);
  const rootTypes = useMemo(
    () => typesFlat.filter((t) => t.id_pai == null || t.id_pai === 0),
    [typesFlat]
  );

  const selectedTypeIds = filters.ids_tipos || [];
  const selectedSubtype1Ids = filters.ids_subtipo1 || [];
  const selectedSubtype2Ids = filters.ids_subtipo2 || [];
  const selectedSubtype3Ids = filters.ids_subtipo3 || [];

  const subtype1Options = useMemo(() => {
    if (!selectedTypeIds.length) return [];
    const idSet = new Set(selectedTypeIds.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedTypeIds]);

  const subtype2Options = useMemo(() => {
    if (!selectedSubtype1Ids.length) return [];
    const idSet = new Set(selectedSubtype1Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype1Ids]);

  const subtype3Options = useMemo(() => {
    if (!selectedSubtype2Ids.length) return [];
    const idSet = new Set(selectedSubtype2Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype2Ids]);

  useEffect(() => {
    if (readOnly) return;
    if (!sendAccountOptions.length) return;
    const current = Number(skillConfig.channel_id || 0);
    const stillValid = sendAccountOptions.some((c) => Number(c.id) === current);
    if (!stillValid) {
      mergeOpsConfig({
        channel_id: Number(sendAccountOptions[0].id),
        whatsapp_templates: emptyWhatsappTemplates(),
        whatsapp_template_id: null,
      });
    }
  }, [deliveryChannel, sendAccountOptions, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!needsWhatsappTemplate || !selectedSendAccount?.id) {
      setWaTemplates([]);
      setWaTemplatesLoading(false);
      setWaTemplatesReady(false);
      waTemplatesLoadedForChannelRef.current = null;
      return undefined;
    }
    const channelId = Number(selectedSendAccount.id);
    let cancelled = false;
    setWaTemplatesReady(false);
    setWaTemplatesLoading(true);
    waTemplatesLoadedForChannelRef.current = null;
    (async () => {
      try {
        const list = await getChannelTemplates(channelId);
        if (cancelled) return;
        setWaTemplates(normalizeChannelTemplatesPayload(list));
        waTemplatesLoadedForChannelRef.current = channelId;
        setWaTemplatesReady(true);
      } catch (_) {
        if (cancelled) return;
        setWaTemplates([]);
        waTemplatesLoadedForChannelRef.current = channelId;
        setWaTemplatesReady(true);
      } finally {
        if (!cancelled) setWaTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsWhatsappTemplate, selectedSendAccount?.id]);

  const compatibleTemplatesByFormat = useMemo(() => {
    const map = {};
    for (const opt of OUTPUT_OPTIONS) {
      const expected = expectedHeaderForFormat(opt.key);
      map[opt.key] = (waTemplates || []).filter((tpl) =>
        isOpsIntelligenceWhatsappTemplateSelectable(tpl, expected)
      );
    }
    return map;
  }, [waTemplates]);

  const selectedTemplatesByFormat = useMemo(() => {
    const map = {};
    for (const opt of OUTPUT_OPTIONS) {
      const id = Number(waTemplatesByFormat[opt.key] || 0);
      if (!id) {
        map[opt.key] = null;
        continue;
      }
      const expected = expectedHeaderForFormat(opt.key);
      const tpl = waTemplates.find((x) => Number(x.id) === id) || null;
      // Só mantém no seletor se for compatível com o formato — incompatível fica null.
      map[opt.key] =
        tpl && isOpsIntelligenceWhatsappTemplateSelectable(tpl, expected) ? tpl : null;
    }
    return map;
  }, [waTemplates, waTemplatesByFormat]);

  useEffect(() => {
    if (readOnly || !needsWhatsappTemplate) return;
    if (!waTemplatesReady || waTemplatesLoading) return;
    if (
      Number(waTemplatesLoadedForChannelRef.current) !== Number(selectedSendAccount?.id || 0)
    ) {
      return;
    }
    let changed = false;
    const next = { ...waTemplatesByFormat };
    for (const formatKey of formats) {
      const id = Number(next[formatKey] || 0);
      if (!id) continue;
      const tpl = waTemplates.find((x) => Number(x.id) === id);
      const expected = expectedHeaderForFormat(formatKey);
      if (!tpl || !isOpsIntelligenceWhatsappTemplateSelectable(tpl, expected)) {
        next[formatKey] = null;
        changed = true;
      }
    }
    // Limpa templates de formatos desmarcados.
    for (const key of Object.keys(next)) {
      if (!formats.includes(key) && next[key]) {
        next[key] = null;
        changed = true;
      }
    }
    if (changed) {
      mergeOpsConfig({
        whatsapp_templates: next,
        whatsapp_template_id: next.text || null,
      });
    }
  }, [
    waTemplates,
    waTemplatesReady,
    waTemplatesLoading,
    needsWhatsappTemplate,
    readOnly,
    selectedSendAccount?.id,
    formats.join(","),
    JSON.stringify(waTemplatesByFormat),
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const configFingerprint = useMemo(() => JSON.stringify(skillConfig || {}), [skillConfig]);

  const statusOptions = useMemo(
    () => QUICK_STATUS_FILTER_LABELS.map((label) => ({ id: label, label })),
    []
  );

  const recipientOptions = useMemo(() => {
    const positionOpts = (positions || []).map((p) => ({
      key: `position:${p.id}`,
      type: "position",
      id: Number(p.id),
      name: p.nome || p.name || `Cargo #${p.id}`,
      group: copy("opsRecipientsGroupPositions", "Cargos"),
    }));
    const userOpts = (users || []).map((u) => ({
      key: `user:${u.id}`,
      type: "user",
      id: Number(u.id),
      name: u.nome || u.name || u.email || `Usuário #${u.id}`,
      email: u.email,
      group: copy("opsRecipientsGroupPeople", "Pessoas"),
    }));
    return [...positionOpts, ...userOpts];
  }, [positions, users, t]);

  const analysisPeopleOptions = useMemo(() => {
    const chatbotIds = new Set(
      (users || []).filter((u) => Number(u.ura) === 1).map((u) => Number(u.id))
    );
    return recipientOptions.filter(
      (o) => o.type === "position" || !chatbotIds.has(Number(o.id))
    );
  }, [recipientOptions, users]);

  const selectedRecipients = useMemo(() => {
    return recipients
      .map((r) => {
        const key = `${r.type}:${r.id}`;
        return recipientOptions.find((o) => o.key === key) || {
          key,
          type: r.type,
          id: Number(r.id),
          name: r.name || (r.type === "position" ? `Cargo #${r.id}` : `Usuário #${r.id}`),
          email: r.email,
          group:
            r.type === "position"
              ? copy("opsRecipientsGroupPositions", "Cargos")
              : copy("opsRecipientsGroupPeople", "Pessoas"),
        };
      })
      .filter(Boolean);
  }, [recipients, recipientOptions, t]);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setEstimating(true);
      setError("");
      try {
        const res = await estimateOpsIntelligence({ config: skillConfig });
        if (!cancelled) setEstimate(res?.data || res || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || copy("opsEstimateError", "Não foi possível estimar o uso"));
      } finally {
        if (!cancelled) setEstimating(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [configFingerprint, readOnly, skillConfig]);

  useEffect(() => {
    if (!currentAgentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getOpsIntelligenceRuns(currentAgentId, { limit: 8 });
        if (!cancelled) setRuns(res?.runs || res?.data?.runs || []);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAgentId, okMsg]);

  const toggleFormat = (key) => {
    if (!isFormatAllowedForChannel(key, deliveryChannel)) return;
    const nextRaw = key === "text" ? ["text"] : ["text", key];
    const { formats: nextFormats } = pruneFormatsForChannel(nextRaw, deliveryChannel);
    const nextTemplates = { ...waTemplatesByFormat };
    for (const opt of OUTPUT_OPTIONS) {
      if (!nextFormats.includes(opt.key)) nextTemplates[opt.key] = null;
    }
    mergeOpsConfig({
      output_formats: nextFormats,
      whatsapp_templates: nextTemplates,
      whatsapp_template_id: nextTemplates.text || null,
    });
  };

  // Ao trocar o canal, remove formatos incompatíveis (ex.: vídeo no SMS).
  useEffect(() => {
    if (readOnly) return;
    const { formats: nextFormats, pruned } = pruneFormatsForChannel(formats, deliveryChannel);
    if (!pruned.length && nextFormats.length === formats.length) return;
    const nextTemplates = { ...waTemplatesByFormat };
    for (const opt of OUTPUT_OPTIONS) {
      if (!nextFormats.includes(opt.key)) nextTemplates[opt.key] = null;
    }
    mergeOpsConfig({
      output_formats: nextFormats,
      whatsapp_templates: nextTemplates,
      whatsapp_template_id: nextTemplates.text || null,
    });
  }, [deliveryChannel, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRunDetail = async (agentId, runId) => {
    if (!agentId || !runId) return null;
    try {
      const res = await getOpsIntelligenceRun(agentId, runId);
      return res?.run || res?.data?.run || null;
    } catch (_) {
      return null;
    }
  };

  const openRunDetail = async (run) => {
    if (!currentAgentId || !run?.id) return;
    setSelectedRunId(run.id);
    setRunDetail(run);
    setRunDetailLoading(true);
    const detail = await loadRunDetail(currentAgentId, run.id);
    setRunDetail(detail || run);
    setRunDetailLoading(false);
  };

  const handleTrigger = async () => {
    if (!currentAgentId || readOnly) return;
    if (needsWhatsappTemplate) {
      const missingPrimary = !Number(waTemplatesByFormat[selectedFormat] || 0);
      if (missingPrimary) {
        setError(
          copy(
            "opsWaTemplatesRequired",
            "Selecione um template WhatsApp aprovado para o formato escolhido."
          )
        );
        return;
      }
    }
    if (!Array.isArray(skillConfig.recipients) || !skillConfig.recipients.length) {
      setError(
        copy(
          "opsTestRecipientsRequired",
          "Inclua pelo menos um destinatário (pessoa ou cargo) para testar o envio."
        )
      );
      return;
    }

    const ticket = ++testAbortRef.current;
    setTriggering(true);
    setError("");
    setOkMsg("");
    setTestRun(null);
    setTestProgress(copy("opsTestProgressStart", "Iniciando análise com IA…"));

    const statusLabel = (status) => {
      const map = {
        pending: copy("opsTestStatusPending", "Na fila"),
        running: copy("opsTestStatusRunning", "Analisando dados…"),
        analyzed: copy("opsTestStatusAnalyzed", "Análise pronta — gerando mídias…"),
        media_ready: copy("opsTestStatusMedia", "Enviando relatório…"),
        delivered: copy("opsTestStatusDelivered", "Entregue"),
        completed: copy("opsTestStatusDelivered", "Entregue"),
        failed: copy("opsTestStatusFailed", "Falhou"),
      };
      return map[String(status || "")] || String(status || "");
    };

    try {
      const res = await triggerOpsIntelligence(currentAgentId, {
        config: skillConfig,
        test: true,
      });
      if (ticket !== testAbortRef.current) return;
      const runId = res?.runId || res?.data?.runId;

      if (res?.async || res?.status === 202) {
        setOkMsg(
          copy(
            "opsTriggerQueued",
            "Relatório enfileirado. Isso pode levar alguns minutos — acompanhe o resultado abaixo."
          )
        );
        const started = Date.now();
        let finalRun = null;
        while (Date.now() - started < 180_000) {
          if (ticket !== testAbortRef.current) return;
          await new Promise((r) => setTimeout(r, 3500));
          if (ticket !== testAbortRef.current) return;
          try {
            const list = await getOpsIntelligenceRuns(currentAgentId, { limit: 5 });
            const nextRuns = list?.runs || list?.data?.runs || [];
            setRuns(nextRuns);
            const mine = runId
              ? nextRuns.find((r) => Number(r.id) === Number(runId))
              : nextRuns[0];
            if (mine) {
              setTestProgress(statusLabel(mine.status));
              if (["delivered", "failed", "completed"].includes(String(mine.status))) {
                finalRun = (await loadRunDetail(currentAgentId, mine.id)) || mine;
                break;
              }
            }
          } catch (_) {
            break;
          }
        }
        if (ticket !== testAbortRef.current) return;
        if (finalRun) {
          setTestRun(finalRun);
          if (String(finalRun.status) === "failed") {
            setError(
              finalRun.error_message || copy("opsTriggerError", "Falha ao gerar relatório")
            );
            setOkMsg("");
          } else {
            setOkMsg(copy("opsTriggerOk", "Relatório gerado e enviado."));
            setTestProgress(statusLabel(finalRun.status));
          }
        } else {
          setOkMsg(
            copy(
              "opsTestStillRunning",
              "O teste continua em segundo plano. Atualize em Últimos envios daqui a pouco."
            )
          );
        }
      } else {
        const syncRunId = runId;
        let detail = syncRunId ? await loadRunDetail(currentAgentId, syncRunId) : null;
        if (!detail && (res?.resumo || res?.data?.resumo)) {
          detail = {
            id: syncRunId,
            status: "delivered",
            analysis_json: {
              resumo: res?.resumo || res?.data?.resumo,
            },
            media_json: { assets: res?.mediaAssets || res?.data?.mediaAssets || [] },
            recipients_json: res?.delivery || res?.data?.delivery || null,
          };
        }
        if (detail) setTestRun(detail);
        setTestProgress(statusLabel(detail?.status || "delivered"));
        setOkMsg(res?.message || copy("opsTriggerOk", "Relatório gerado."));
        const list = await getOpsIntelligenceRuns(currentAgentId, { limit: 8 });
        setRuns(list?.runs || list?.data?.runs || []);
      }
    } catch (e) {
      if (ticket !== testAbortRef.current) return;
      setError(e?.message || copy("opsTriggerError", "Falha ao gerar relatório"));
      setTestProgress("");
    } finally {
      if (ticket === testAbortRef.current) setTriggering(false);
    }
  };

  const testAnalysis = testRun?.analysis_json || null;
  const testMedia = testRun?.media_json || null;
  const testDelivery = testRun?.recipients_json || null;
  const testThreads = testDelivery?.threads || [];
  const testSpaceHit =
    (testThreads || []).find((t) => t?.spaceId) ||
    (testDelivery?.deliveries || []).find((d) => d?.spaceId);
  const testSpaceId = testSpaceHit?.spaceId ? Number(testSpaceHit.spaceId) : null;
  const testBriefing =
    testAnalysis?.briefing_gestor ||
    testMedia?.text ||
    testAnalysis?.resumo ||
    "";
  const testAssets = Array.isArray(testMedia?.assets) ? testMedia.assets : [];
  const testMediaErrors = Array.isArray(testMedia?.errors) ? testMedia.errors : [];
  const testDeliveryRows = Array.isArray(testDelivery?.deliveries)
    ? testDelivery.deliveries
    : [];
  const testDmFailed = testDeliveryRows.some((d) => d?.dm && d.dm.ok === false);
  const testWaFailed = testDeliveryRows.some(
    (d) =>
      d?.channel &&
      d.channel.ok === false &&
      String(d.deliveryChannel || testDelivery?.deliveryChannel || "") === "whatsapp"
  );
  const testWaMediaNote = testDeliveryRows.some(
    (d) =>
      d?.channel?.warning === "official_whatsapp_template_is_text_only_media_best_effort" ||
      d?.channel?.mediaFollowUp?.ok === false
  );

  const usageMonth = estimate?.usage_per_month || {};
  const plan = estimate?.plan || {};
  const aiLimit = Number(plan.ai_credits_limit || 0);
  const aiUsed = Number(plan.ai_credits_used || 0);
  const aiProj = Number(usageMonth.ai_credits || 0);
  const aiPct = aiLimit > 0 ? Math.min(100, Math.round(((aiUsed + aiProj) / aiLimit) * 100)) : null;
  const planHeadroomPct =
    aiLimit > 0 ? Math.max(0, Math.round(100 - ((aiUsed + aiProj) / aiLimit) * 100)) : null;

  const scheduleUnit = normalizeScheduleUnit(skillConfig.schedule_interval?.unit);
  const scheduleValue = Math.max(1, Number(skillConfig.schedule_interval?.value) || 1);
  const scheduleTime = toTimeInputValue(skillConfig.schedule_time || "08:00");

  // Garante que o horário exibido na copy também fique persistido no config_json.
  useEffect(() => {
    if (readOnly) return;
    if (skillConfig.schedule_time) return;
    mergeOpsConfig({ schedule_time: scheduleTime });
  }, [readOnly, skillConfig.schedule_time]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodUnit = normalizeScheduleUnit(skillConfig.custom_relative?.unit);

  const typeLabel = (o) => o?.nome || titleOfType(o) || `#${o?.id}`;
  const channelLabel = (o) => o?.nome || o?.name || `Canal #${o?.id}`;

  const patchTypes = (ids) => {
    const nextIds = ids || [];
    const idSet = new Set(nextIds.map(Number));
    const nextS1 = (filters.ids_subtipo1 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && idSet.has(Number(node.id_pai));
    });
    const s1Set = new Set(nextS1.map(Number));
    const nextS2 = (filters.ids_subtipo2 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && s1Set.has(Number(node.id_pai));
    });
    const s2Set = new Set(nextS2.map(Number));
    const nextS3 = (filters.ids_subtipo3 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && s2Set.has(Number(node.id_pai));
    });
    mergeOpsConfig({
      filters: mergeFilters(filters, {
        ids_tipos: nextIds,
        ids_subtipo1: nextS1,
        ids_subtipo2: nextS2,
        ids_subtipo3: nextS3,
      }),
    });
  };

  const patchSubtype1 = (ids) => {
    const nextIds = ids || [];
    const idSet = new Set(nextIds.map(Number));
    const nextS2 = (filters.ids_subtipo2 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && idSet.has(Number(node.id_pai));
    });
    const s2Set = new Set(nextS2.map(Number));
    const nextS3 = (filters.ids_subtipo3 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && s2Set.has(Number(node.id_pai));
    });
    mergeOpsConfig({
      filters: mergeFilters(filters, {
        ids_subtipo1: nextIds,
        ids_subtipo2: nextS2,
        ids_subtipo3: nextS3,
      }),
    });
  };

  const patchSubtype2 = (ids) => {
    const nextIds = ids || [];
    const idSet = new Set(nextIds.map(Number));
    const nextS3 = (filters.ids_subtipo3 || []).filter((id) => {
      const node = typesFlat.find((t) => Number(t.id) === Number(id));
      return node && idSet.has(Number(node.id_pai));
    });
    mergeOpsConfig({
      filters: mergeFilters(filters, {
        ids_subtipo2: nextIds,
        ids_subtipo3: nextS3,
      }),
    });
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.35 }}>
          {t("agentsListPage.skillOpsIntelligence")}
        </Typography>
        <Typography variant="body2">
          {t("agentsListPage.skillOpsIntelligenceIntro")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {copy(
            "opsLifecycleNotUsed",
            "O relatório não altera status de solicitações. O envio é no horário agendado; perguntas ficam no chat da equipe."
          )}
        </Typography>
      </Alert>

      <ConfigAccordion
        title={copy("opsSectionScopeTitle", "Quais atendimentos analisar")}
        subtitle={copy(
          "opsSectionScopeSubtitle",
          "Filtre tipos, canais, responsáveis e quem interagiu. O período recorta novas (entrada) e trabalhadas (última atualização). A fila em aberto entra mesmo que tenha começado antes."
        )}
        defaultExpanded
      >
        <Stack spacing={2}>
          <MultiSelectField
            label={copy("opsFilterTypes", "Tipos de atendimento")}
            helperText={copy("opsFilterTypesHelper", "Vazio = todos os tipos")}
            placeholder={copy("opsSelectPlaceholder", "Selecione...")}
            options={rootTypes}
            valueIds={selectedTypeIds}
            onChangeIds={patchTypes}
            getOptionLabel={typeLabel}
            disabled={readOnly}
            loading={typesLoading}
          />

          {subtype1Options.length > 0 ? (
            <MultiSelectField
              label={copy("opsFilterSubtype1", "Subtipo 1")}
              helperText={copy("opsFilterSubtypeHelper", "Aparece conforme o tipo escolhido. Vazio = todos deste nível.")}
              placeholder={copy("opsSelectPlaceholder", "Selecione...")}
              options={subtype1Options}
              valueIds={selectedSubtype1Ids}
              onChangeIds={patchSubtype1}
              getOptionLabel={typeLabel}
              disabled={readOnly}
              loading={typesLoading}
            />
          ) : null}

          {subtype2Options.length > 0 ? (
            <MultiSelectField
              label={copy("opsFilterSubtype2", "Subtipo 2")}
              helperText={copy("opsFilterSubtypeHelper", "Aparece conforme o tipo escolhido. Vazio = todos deste nível.")}
              placeholder={copy("opsSelectPlaceholder", "Selecione...")}
              options={subtype2Options}
              valueIds={selectedSubtype2Ids}
              onChangeIds={patchSubtype2}
              getOptionLabel={typeLabel}
              disabled={readOnly}
              loading={typesLoading}
            />
          ) : null}

          {subtype3Options.length > 0 ? (
            <MultiSelectField
              label={copy("opsFilterSubtype3", "Subtipo 3")}
              helperText={copy("opsFilterSubtypeHelper", "Aparece conforme o tipo escolhido. Vazio = todos deste nível.")}
              placeholder={copy("opsSelectPlaceholder", "Selecione...")}
              options={subtype3Options}
              valueIds={selectedSubtype3Ids}
              onChangeIds={(ids) =>
                mergeOpsConfig({
                  filters: mergeFilters(filters, { ids_subtipo3: ids }),
                })
              }
              getOptionLabel={typeLabel}
              disabled={readOnly}
              loading={typesLoading}
            />
          ) : null}

          <MultiSelectField
            label={copy("opsFilterChannels", "Canais / origens")}
            helperText={copy("opsFilterChannelsHelper", "Vazio = todos os canais")}
            placeholder={copy("opsSelectPlaceholder", "Selecione...")}
            options={channels}
            valueIds={filters.ids_origens || []}
            onChangeIds={(ids) =>
              mergeOpsConfig({ filters: mergeFilters(filters, { ids_origens: ids }) })
            }
            getOptionLabel={channelLabel}
            disabled={readOnly}
            loading={channelsLoading}
          />

          <PeopleOrPositionField
            label={copy("opsFilterOwners", "Responsáveis pelas solicitações")}
            helperText={copy(
              "opsFilterOwnersHelper",
              "Quem está atribuído à solicitação. Escolha pessoas e/ou cargos (o cargo inclui todos os usuários daquele cargo). Vazio = qualquer responsável."
            )}
            placeholder={copy("opsSelectPlaceholder", "Selecione...")}
            options={analysisPeopleOptions}
            userIds={filters.ids_usuarios || []}
            cargoIds={filters.ids_cargos || []}
            onChange={({ userIds, cargoIds }) =>
              mergeOpsConfig({
                filters: mergeFilters(filters, {
                  ids_usuarios: userIds,
                  ids_cargos: cargoIds,
                }),
              })
            }
            disabled={readOnly}
            loading={usersLoading || positionsLoading}
            positionPrefix={copy("opsRecipientRolePrefix", "Cargo")}
          />

          <PeopleOrPositionField
            label={copy("opsFilterInteractors", "Quem interagiu nas solicitações")}
            helperText={copy(
              "opsFilterInteractorsHelper",
              "Quem enviou mensagem na solicitação. Escolha pessoas e/ou cargos. Vazio = qualquer interação."
            )}
            placeholder={copy("opsSelectPlaceholder", "Selecione...")}
            options={analysisPeopleOptions}
            userIds={filters.ids_usuarios_interacao || []}
            cargoIds={filters.ids_cargos_interacao || []}
            onChange={({ userIds, cargoIds }) =>
              mergeOpsConfig({
                filters: mergeFilters(filters, {
                  ids_usuarios_interacao: userIds,
                  ids_cargos_interacao: cargoIds,
                }),
              })
            }
            disabled={readOnly}
            loading={usersLoading || positionsLoading}
            positionPrefix={copy("opsRecipientRolePrefix", "Cargo")}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel>{copy("opsPeriod", "Período do panorama")}</InputLabel>
              <Select
                label={copy("opsPeriod", "Período do panorama")}
                value={PERIOD_OPTIONS.some((o) => o.key === skillConfig.period)
                  ? skillConfig.period
                  : "last_7d"}
                onChange={(e) => mergeOpsConfig({ period: e.target.value })}
              >
                {PERIOD_OPTIONS.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {copy(o.labelKey, o.key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {skillConfig.period === "custom_relative" ? (
              <>
                <TextField
                  type="number"
                  size="small"
                  disabled={readOnly}
                  label={copy("opsPeriodCustomValue", "Últimos")}
                  inputProps={{ min: 1, max: 90 }}
                  sx={{ width: { xs: "100%", sm: 120 } }}
                  value={skillConfig.custom_relative?.value ?? 7}
                  onChange={(e) =>
                    mergeOpsConfig({
                      custom_relative: {
                        value: Math.max(1, Number(e.target.value) || 7),
                        unit: periodUnit,
                      },
                    })
                  }
                />
                <FormControl size="small" fullWidth disabled={readOnly}>
                  <InputLabel>{copy("opsPeriodCustomUnit", "Unidade do período")}</InputLabel>
                  <Select
                    label={copy("opsPeriodCustomUnit", "Unidade do período")}
                    value={periodUnit}
                    onChange={(e) =>
                      mergeOpsConfig({
                        custom_relative: {
                          value: skillConfig.custom_relative?.value || 7,
                          unit: e.target.value,
                        },
                      })
                    }
                  >
                    {SCHEDULE_UNIT_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {copy(o.labelKey, o.fallback)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : null}
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel>{copy("opsFocus", "Foco do relatório")}</InputLabel>
              <Select
                label={copy("opsFocus", "Foco do relatório")}
                value={skillConfig.focus || "resultado"}
                onChange={(e) => mergeOpsConfig({ focus: e.target.value })}
              >
                <MenuItem value="resultado">{copy("opsFocusResult", "Resultados")}</MenuItem>
                <MenuItem value="qualidade">{copy("opsFocusQuality", "Qualidade do atendimento")}</MenuItem>
                <MenuItem value="conversao">{copy("opsFocusConversion", "Conversões")}</MenuItem>
                <MenuItem value="capacidade">{copy("opsFocusCapacity", "Capacidade da equipe")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {copy(
              "opsPeriodHelper",
              "Neste recorte o relatório mostra o que entrou (nova solicitação) e o que foi trabalhado (data da última atualização), além de tipos, canais e equipe filtrados."
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {copy("opsFocusHelper", "Opcional — puxa a leitura da IA para resultados, qualidade, conversão ou capacidade.")}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {copy("opsOpenQueueTitle", "Fila em aberto")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {copy(
                    "opsOpenQueueHelper",
                    "Estoque atual, sem filtro de data. Ex.: todas as pendentes e em atendimento, mesmo que tenham entrado antes do período."
                  )}
                </Typography>
              </Box>
              <Switch
                checked={Boolean(analysisScope.open_queue.enabled)}
                disabled={readOnly}
                onChange={(e) =>
                  patchAnalysisScope({
                    ...analysisScope,
                    open_queue: { ...analysisScope.open_queue, enabled: e.target.checked },
                  })
                }
              />
            </Stack>
            {analysisScope.open_queue.enabled ? (
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                sx={{ mt: 1.5 }}
                options={statusOptions}
                value={statusOptions.filter((o) =>
                  (analysisScope.open_queue.statuses || []).includes(o.id)
                )}
                disabled={readOnly}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, next) =>
                  patchAnalysisScope({
                    ...analysisScope,
                    open_queue: {
                      ...analysisScope.open_queue,
                      statuses: next.map((o) => o.id),
                    },
                  })
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.label} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={copy("opsOpenQueueStatuses", "Status da fila")}
                    helperText={copy("opsOpenQueueStatusesHelper", "Padrão: Pendente e Em atendimento.")}
                    size="small"
                  />
                )}
              />
            ) : null}
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {copy("opsMovementTitle", "Movimentação do período")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {copy(
                    "opsMovementHelper",
                    "Concluídas, perdidas e contato futuro só do que foi trabalhado no período (data da última atualização)."
                  )}
                </Typography>
              </Box>
              <Switch
                checked={Boolean(analysisScope.period_movement.enabled)}
                disabled={readOnly}
                onChange={(e) =>
                  patchAnalysisScope({
                    ...analysisScope,
                    period_movement: {
                      ...analysisScope.period_movement,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
            </Stack>
            {analysisScope.period_movement.enabled ? (
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                sx={{ mt: 1.5 }}
                options={statusOptions}
                value={statusOptions.filter((o) =>
                  (analysisScope.period_movement.statuses || []).includes(o.id)
                )}
                disabled={readOnly}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, next) =>
                  patchAnalysisScope({
                    ...analysisScope,
                    period_movement: {
                      ...analysisScope.period_movement,
                      statuses: next.map((o) => o.id),
                    },
                  })
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.label} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={copy("opsMovementStatuses", "Status da movimentação")}
                    helperText={copy(
                      "opsMovementStatusesHelper",
                      "Padrão: Concluída, Perdida e Contato futuro. Sempre pela última atualização no período."
                    )}
                    size="small"
                  />
                )}
              />
            ) : null}
          </Box>
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={copy("opsSectionScheduleTitle", "Quando enviar")}
        subtitle={copy(
          "opsSectionScheduleSubtitle",
          "Com que frequência e a que horas o resumo sai."
        )}
        defaultExpanded
      >
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 0.85fr) minmax(0, 1.15fr)",
                sm: "minmax(104px, 0.7fr) minmax(148px, 1fr) minmax(148px, 0.9fr)",
              },
              gap: 1.5,
              alignItems: "start",
            }}
          >
            <TextField
              type="number"
              size="small"
              fullWidth
              disabled={readOnly}
              label={copy("opsScheduleEvery", "A cada")}
              inputProps={{ min: 1, max: 365, step: 1 }}
              value={scheduleValue}
              onChange={(e) =>
                mergeOpsConfig({
                  schedule_interval: {
                    value: Math.max(1, Number(e.target.value) || 1),
                    unit: scheduleUnit,
                  },
                })
              }
            />
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel>{copy("opsScheduleUnit", "Unidade")}</InputLabel>
              <Select
                label={copy("opsScheduleUnit", "Unidade")}
                value={scheduleUnit}
                onChange={(e) =>
                  mergeOpsConfig({
                    schedule_interval: {
                      value: scheduleValue,
                      unit: e.target.value,
                    },
                  })
                }
              >
                {SCHEDULE_UNIT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {scheduleUnitOptionLabel(o, scheduleValue, copy)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={copy("opsScheduleTime", "Horário")}
              type="time"
              size="small"
              fullWidth
              disabled={readOnly}
              InputLabelProps={{ shrink: true }}
              value={scheduleTime}
              onChange={(e) =>
                mergeOpsConfig({ schedule_time: toTimeInputValue(e.target.value) })
              }
              sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <AccessTimeIcon fontSize="small" color="action" sx={{ mt: "2px", flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                {scheduleSummaryText({
                  n: scheduleValue,
                  unit: scheduleUnit,
                  time: scheduleTime,
                  t,
                  copy,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {copy(
                  "opsScheduleHint",
                  "O envio começa neste horário no dia agendado. 1 + Semanas = uma vez por semana."
                )}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={copy("opsSectionDeliveryTitle", "Como entregar")}
        subtitle={copy(
          "opsSectionDeliverySubtitle",
          "Escolha um formato. O texto do briefing sempre acompanha áudio, imagem ou vídeo."
        )}
        defaultExpanded
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
              {copy("opsFormats", "Formato do resumo")}
            </Typography>
            <RadioGroup
              row
              value={selectedFormat}
              onChange={(e) => toggleFormat(e.target.value)}
            >
              {OUTPUT_OPTIONS.map((o) => {
                const allowed = isFormatAllowedForChannel(o.key, deliveryChannel);
                return (
                  <FormControlLabel
                    key={o.key}
                    value={o.key}
                    disabled={readOnly || !allowed}
                    control={<Radio size="small" />}
                    label={
                      <Typography
                        variant="body2"
                        color={!allowed ? "text.disabled" : "text.primary"}
                      >
                        {copy(o.labelKey, o.fallback || o.key)}
                        {!allowed
                          ? ` (${copy("opsFormatUnavailable", "indisponível neste canal")})`
                          : ""}
                      </Typography>
                    }
                  />
                );
              })}
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              {copy(
                "opsFormatsOneHelper",
                "Uma opção apenas. Se escolher mídia, o texto do briefing segue no mesmo envio (e-mail, chat e corpo do template)."
              )}{" "}
              {copy("opsFormatsHintChannel", channelFormatHint(deliveryChannel))}
            </Typography>
            {needsWhatsappTemplate && mediaFormatsSelected ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {copy(
                  "opsWaMediaVsTemplate",
                  "Escolha um template Utilidade aprovado com o Header certo: texto = None/Text; gráfico/infográfico = Image; vídeos = Video; áudio = Document."
                )}
              </Alert>
            ) : null}
            {deliveryChannel === "whatsapp" &&
            selectedSendAccount &&
            !isWhatsAppOfficialChannelTipo(selectedSendAccount.tipo) &&
            mediaFormatsSelected ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {copy(
                  "opsWaQrMediaHint",
                  "Este WhatsApp (QR) não usa template: o XPro envia texto e mídias como mensagens de sessão."
                )}
              </Alert>
            ) : null}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel>{copy("opsDeliveryChannel", "Enviar por")}</InputLabel>
              <Select
                label={copy("opsDeliveryChannel", "Enviar por")}
                value={deliveryChannel}
                onChange={(e) =>
                  {
                    const nextChannel = e.target.value;
                    const { formats: nextFormats } = pruneFormatsForChannel(
                      formats,
                      nextChannel
                    );
                    const nextTemplates = emptyWhatsappTemplates();
                    const primary = primaryFormatOf(nextFormats);
                    if (waTemplatesByFormat.text) {
                      nextTemplates.text = waTemplatesByFormat.text;
                    }
                    if (primary !== "text" && waTemplatesByFormat[primary]) {
                      nextTemplates[primary] = waTemplatesByFormat[primary];
                    }
                    mergeOpsConfig({
                      delivery_channel: nextChannel,
                      channel_id: null,
                      output_formats: nextFormats,
                      whatsapp_templates: nextTemplates,
                      whatsapp_template_id: nextTemplates.text || null,
                    });
                  }
                }
              >
                <MenuItem value="email">{copy("opsDeliveryEmail", "E-mail")}</MenuItem>
                <MenuItem value="whatsapp">{copy("opsDeliveryWhatsapp", "WhatsApp")}</MenuItem>
                <MenuItem value="sms">{copy("opsDeliverySms", "SMS")}</MenuItem>
                <MenuItem value="voice">{copy("opsDeliveryVoice", "Telefone")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={readOnly || channelsLoading}>
              <InputLabel>{copy("opsSendAccount", "Conta de envio")}</InputLabel>
              <Select
                label={copy("opsSendAccount", "Conta de envio")}
                value={
                  sendAccountOptions.some((c) => Number(c.id) === Number(skillConfig.channel_id))
                    ? skillConfig.channel_id
                    : ""
                }
                onChange={(e) =>
                  mergeOpsConfig({
                    channel_id: e.target.value === "" ? null : Number(e.target.value),
                    whatsapp_templates: emptyWhatsappTemplates(),
                    whatsapp_template_id: null,
                  })
                }
              >
                {!sendAccountOptions.length ? (
                  <MenuItem value="">
                    <em>{copy("opsSendAccountEmpty", "Nenhuma conta deste tipo cadastrada")}</em>
                  </MenuItem>
                ) : (
                  sendAccountOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {channelLabel(c)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Stack>

          {needsWhatsappTemplate ? (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {copy("opsWaTemplatesTitle", "Template WhatsApp")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {copy(
                  "opsWaTemplatesSubtitle",
                  "Um template para o formato escolhido. A lista filtra pelo tipo de Header exigido pela Meta/Gupshup."
                )}
              </Typography>

              {OUTPUT_OPTIONS.filter((o) => o.key === selectedFormat).map((o) => {
                const expected = expectedHeaderForFormat(o.key);
                const compatible = compatibleTemplatesByFormat[o.key] || [];
                // Nunca inclui template incompatível na lista — só os filtrados pelo Header.
                const options = compatible;
                const selected = selectedTemplatesByFormat[o.key];
                const preview = selected
                  ? buildOpsIntelligenceDeliveryPreview(
                      selected,
                      {
                        name: copy("opsWaPreviewSampleName", "Ana Silva"),
                        locale: String(locale || "pt").toLowerCase().startsWith("es")
                          ? "es"
                          : String(locale || "pt").toLowerCase().startsWith("en")
                            ? "en"
                            : "pt",
                        formats,
                      },
                      { expectedHeader: expected, formatKey: o.key, formats }
                    )
                  : null;
                const formatLabel = copy(o.labelKey, o.fallback || o.key);
                const headerLabel = HEADER_KIND_LABELS[expected] || expected;
                const sampleCopy = sampleTemplateCopyForFormat(o.key);
                const visibleFormatKeys = OUTPUT_OPTIONS.filter((opt) =>
                  formats.includes(opt.key)
                ).map((opt) => opt.key);
                const messageIndex = Math.max(1, visibleFormatKeys.indexOf(o.key) + 1);
                return (
                  <Box
                    key={o.key}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "grey.50",
                    }}
                  >
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {copy("opsWaTemplateForFormat", "Template — {{format}}").replace(
                            "{{format}}",
                            formatLabel
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {FORMAT_HEADER_HINT[o.key] || `Header necessário: ${headerLabel}`}
                        </Typography>
                      </Box>
                      <Autocomplete
                        size="small"
                        disabled={readOnly || waTemplatesLoading}
                        loading={waTemplatesLoading}
                        options={options}
                        value={selected}
                        getOptionLabel={(tpl) => templateDisplayName(tpl)}
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        getOptionDisabled={(tpl) =>
                          !isOpsIntelligenceWhatsappTemplateSelectable(tpl, expected)
                        }
                        filterOptions={(opts) =>
                          opts.filter((tpl) =>
                            isOpsIntelligenceWhatsappTemplateSelectable(tpl, expected)
                          )
                        }
                        noOptionsText={copy(
                          "opsWaTemplateEmptyShort",
                          "Nenhum template compatível com este Header"
                        )}
                        onChange={(_, next) => {
                          if (!next) {
                            setWhatsappTemplateForFormat(o.key, null);
                            return;
                          }
                          if (!isOpsIntelligenceWhatsappTemplateSelectable(next, expected)) {
                            setError(
                              copy(
                                "opsWaTemplateIncompatiblePick",
                                "Esse template não é compatível com o formato {{format}} (Header {{header}})."
                              )
                                .replace("{{format}}", formatLabel)
                                .replace("{{header}}", headerLabel)
                            );
                            return;
                          }
                          setWhatsappTemplateForFormat(o.key, Number(next.id));
                        }}
                        renderOption={(props, tpl) => {
                          const body = tpl.bodyText || tpl.preview || "";
                          return (
                            <Box
                              component="li"
                              {...props}
                              key={tpl.id}
                              sx={{ alignItems: "flex-start !important" }}
                            >
                              <Box sx={{ py: 0.5, width: "100%" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {templateDisplayName(tpl)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {templateMetaLine(tpl, copy) ||
                                    copy("opsWaTemplateApproved", "Aprovado")}
                                </Typography>
                                {body ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{
                                      mt: 0.35,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {body}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={copy("opsWaTemplate", "Template WhatsApp")}
                            placeholder={copy(
                              "opsWaTemplateNone",
                              "Selecione um template no padrão"
                            )}
                            helperText={
                              waTemplatesLoading
                                ? copy("opsWaTemplateLoading", "Carregando templates...")
                                : copy(
                                    "opsWaTemplateHelperByHeader",
                                    "Lista só templates aprovados de utilidade com Header {{header}}. Incompatíveis não aparecem."
                                  ).replace("{{header}}", headerLabel)
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {waTemplatesLoading ? (
                                    <CircularProgress color="inherit" size={16} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      {!waTemplatesLoading && compatible.length === 0 ? (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {copy(
                            "opsWaTemplateEmptyForHeader",
                            "Nenhum template com Header {{header}} neste canal. Crie na Meta/Gupshup (Utilidade + Header {{header}}), aprove e importe."
                          ).replaceAll("{{header}}", headerLabel)}
                        </Alert>
                      ) : null}
                      {preview?.filledText ? (
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.5 }}
                          >
                            {copy("opsWaPreviewTitle", "Como chega no WhatsApp")}
                            {formats.length > 1
                              ? ` · ${copy(
                                  "opsWaPreviewMessageOf",
                                  "mensagem {{n}} de {{total}}"
                                )
                                  .replace("{{n}}", String(messageIndex))
                                  .replace("{{total}}", String(visibleFormatKeys.length))}`
                              : ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 1 }}
                          >
                            {copy(
                              "opsWaPreviewHint",
                              "Prévia do template com o briefing como a Meta exige nas variáveis (sem quebra de linha no {{1}}/{{2}}). No disparo os números vêm da operação; cada formato marcado vira uma mensagem separada."
                            )}
                          </Typography>
                          {preview.headerMedia ? (
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.100",
                                border: "1px dashed",
                                borderColor: "divider",
                              }}
                            >
                              <Typography variant="caption" fontWeight={600} display="block">
                                {copy("opsWaPreviewHeaderLabel", "Header")} ·{" "}
                                {String(preview.headerMedia.type || "").toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {preview.headerMedia.label}
                                {preview.headerMedia.filename
                                  ? ` · ${preview.headerMedia.filename}`
                                  : ""}
                                {preview.headerMedia.mimeHint
                                  ? ` (${preview.headerMedia.mimeHint})`
                                  : ""}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 1 }}
                            >
                              {copy(
                                "opsWaPreviewNoHeaderMedia",
                                "Sem mídia no Header (None/Text) — só o corpo do template."
                              )}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.75 }}
                          >
                            {(preview.params?.length || 0) <= 1
                              ? copy(
                                  "opsWaPreviewMappingOne",
                                  "{{1}} = briefing operacional do período."
                                )
                              : copy(
                                  "opsWaPreviewMapping",
                                  "{{1}} = nome de quem recebe · {{2}} = briefing operacional."
                                )}
                          </Typography>
                          <Typography
                            component="pre"
                            variant="body2"
                            sx={{
                              m: 0,
                              whiteSpace: "pre-wrap",
                              fontFamily: "inherit",
                              lineHeight: 1.5,
                            }}
                          >
                            {preview.filledText}
                          </Typography>
                          {formats.length > 1 ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              {copy(
                                "opsWaPreviewMultiFormatNote",
                                "Os outros formatos marcados chegam em mensagens seguintes (Header Image/Video/Document). Botões Infográfico/Áudio/Vídeo no template reenviam a mídia pelo agente de inteligência operacional — não pelo chatbot de leads. Evite links S3 no body."
                              )}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : null}
                      {sampleCopy ? (
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: "1px dashed",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            display="block"
                            sx={{ mb: 0.5 }}
                          >
                            {copy(
                              "opsWaSampleForFormatTitle",
                              "Como criar o template deste formato"
                            )}
                          </Typography>
                          <Typography
                            component="pre"
                            variant="caption"
                            color="text.secondary"
                            sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                          >
                            {sampleCopy}
                          </Typography>
                        </Box>
                      ) : null}
                      <OpsTemplateFeedback
                        template={selected}
                        t={t}
                        expectedHeader={expected}
                      />
                    </Stack>
                  </Box>
                );
              })}

              <Box>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setWaGuideOpen((v) => !v)}
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {copy("opsWaGuideTitle", "Resumo: como criar na Meta/Gupshup")}
                  {waGuideOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </Link>
                <Collapse in={waGuideOpen}>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {copy(
                        "opsWaGuideIntro",
                        "Crie um template Utilidade por formato marcado, com o Header certo. Aguarde Approved e importe no canal. O resumo abaixo só lista o que bate com os formatos selecionados."
                      )}
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 2.25, color: "text.secondary", typography: "body2" }}
                    >
                      <li>{copy("opsWaGuideBullet1", "Status: Approved / Active.")}</li>
                      <li>
                        {copy(
                          "opsWaGuideBullet2",
                          "Categoria Utilidade (não autenticação nem marketing)."
                        )}
                      </li>
                      {formats.includes("text") ? (
                        <li>
                          {copy(
                            "opsWaGuideBullet3",
                            "Texto: Header None/Text com {{1}} (ideal {{1}} nome + {{2}} resumo)."
                          )}
                        </li>
                      ) : null}
                      {formats.some((f) => f === "image_chart" || f === "infographic") ? (
                        <li>
                          {copy(
                            "opsWaGuideBulletImage",
                            "Gráfico / Infográfico: Header Image."
                          )}
                        </li>
                      ) : null}
                      {formats.some((f) => f === "video_narrated" || f === "video_animated") ? (
                        <li>
                          {copy(
                            "opsWaGuideBulletVideo",
                            "Vídeo explicativo / animado: Header Video."
                          )}
                        </li>
                      ) : null}
                      {formats.includes("audio") ? (
                        <li>
                          {copy(
                            "opsWaGuideBulletAudio",
                            "Áudio: Header Document (não existe Header Áudio)."
                          )}
                        </li>
                      ) : null}
                    </Box>
                    {[...new Set(formats.map((f) => expectedHeaderForFormat(f)))].map(
                      (headerKey) => {
                        const sample = OPS_WA_SAMPLE_BY_HEADER[headerKey];
                        if (!sample) return null;
                        return (
                          <Box
                            key={headerKey}
                            sx={{
                              p: 1.25,
                              borderRadius: 1,
                              border: "1px dashed",
                              borderColor: "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              display="block"
                              sx={{ mb: 0.75 }}
                            >
                              {copy(
                                "opsWaGuideSampleHeaderTitle",
                                "Modelo — Header {{header}}"
                              ).replace(
                                "{{header}}",
                                HEADER_KIND_LABELS[headerKey] || headerKey
                              )}
                            </Typography>
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                            >
                              {sample}
                            </Typography>
                          </Box>
                        );
                      }
                    )}
                  </Stack>
                </Collapse>
              </Box>
            </Stack>
          ) : null}

          {!sendAccountOptions.length ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {copy(
                "opsSendAccountRequired",
                "Cadastre uma conta compatível (e-mail, WhatsApp, SMS ou telefone) em Canais para enviar o resumo."
              )}
            </Alert>
          ) : null}
          {deliveryChannel === "voice" ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {copy(
                "opsDeliveryVoiceHint",
                "A ligação toca o áudio do resumo (ou lê o texto) e o relatório completo fica no chat da equipe."
              )}
            </Typography>
          ) : null}
          {deliveryChannel === "whatsapp" &&
          selectedSendAccount &&
          !isWhatsAppOfficialChannelTipo(selectedSendAccount.tipo) ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {copy(
                "opsWaQrHint",
                "WhatsApp por QR não exige template: o resumo é enviado como mensagem de sessão."
              )}
            </Typography>
          ) : null}
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={copy("opsSectionRecipientsTitle", "Quem recebe")}
        subtitle={copy(
          "opsSectionRecipientsSubtitle",
          "Pessoas ou cargos. Quem tiver o cargo selecionado também recebe."
        )}
        defaultExpanded
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          groupBy={(o) => o.group}
          options={recipientOptions}
          value={selectedRecipients}
          loading={usersLoading || positionsLoading}
          disabled={readOnly}
          getOptionLabel={(o) => o.name || ""}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          onChange={(_, next) =>
            mergeOpsConfig({
              recipients: next.map((o) => ({
                type: o.type,
                id: o.id,
                name: o.name,
                ...(o.email ? { email: o.email } : {}),
              })),
            })
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.key}
                size="small"
                color={option.type === "position" ? "primary" : "default"}
                variant={option.type === "position" ? "filled" : "outlined"}
                label={
                  option.type === "position"
                    ? `${copy("opsRecipientRolePrefix", "Cargo")}: ${option.name}`
                    : option.name
                }
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={copy("opsRecipients", "Destinatários")}
              helperText={copy(
                "opsRecipientsHelper",
                "Digite para buscar por nome de pessoa ou cargo."
              )}
              placeholder={
                selectedRecipients.length
                  ? undefined
                  : copy("opsRecipientsPlaceholder", "Buscar pessoas ou cargos...")
              }
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading || positionsLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </ConfigAccordion>

      <ConfigAccordion
        title={copy("opsSectionAdvancedTitle", "Avançado")}
        subtitle={copy("opsSectionAdvancedSubtitle", "Ajustes opcionais de estimativa.")}
      >
        <TextField
          type="number"
          size="small"
          fullWidth
          disabled={readOnly}
          inputProps={{ min: 0, max: 20 }}
          label={copy("opsFollowups", "Perguntas extras esperadas por relatório")}
          helperText={copy(
            "opsFollowupsHelper",
            "Usado só para estimar o uso do plano quando a equipe responde no chat."
          )}
          value={skillConfig.estimated_followup_turns_per_run ?? 3}
          onChange={(e) =>
            mergeOpsConfig({
              estimated_followup_turns_per_run: Math.max(0, Number(e.target.value) || 0),
            })
          }
        />
      </ConfigAccordion>

      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.35 }}>
          {copy("opsEstimateTitle", "Em resumo: o que isso consome")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {copy(
            "opsEstimateLead",
            "Números aproximados com base nos filtros e no volume recente — para você decidir se a frequência e os formatos fazem sentido."
          )}
        </Typography>
        {estimating && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}
        {estimate ? (
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {copy("opsEstimateReachLabel", "Atendimentos que entram no relatório")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatInt(estimate.reach?.avg ?? estimate.reach?.current, locale)}
                {estimate.reach?.current != null &&
                Number(estimate.reach.current) !== Number(estimate.reach.avg)
                  ? ` ${copy("opsEstimateReachNowParen", "(neste período: {{n}})")}`.replace(
                      "{{n}}",
                      formatInt(estimate.reach.current, locale)
                    )
                  : ""}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {copy("opsEstimateSendsLabel", "Quantas vezes o resumo sai por mês")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatApprox(estimate.schedule?.runs_per_month, locale)}
                {" · "}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                  {copy("opsEstimatePeopleLabel", "{{n}} pessoa(s) recebendo").replace(
                    "{{n}}",
                    formatInt(estimate.recipients?.resolved ?? 0, locale)
                  )}
                </Typography>
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {copy("opsEstimatePlanUseLabel", "Uso estimado do plano de IA")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {aiPct != null
                  ? copy(
                      "opsEstimatePlanUseValue",
                      "Cerca de {{pct}}% do seu limite mensal com esta configuração"
                    ).replace("{{pct}}", String(aiPct))
                  : copy(
                      "opsEstimatePlanUseCredits",
                      "Cerca de {{n}} unidades de IA por mês"
                    ).replace("{{n}}", formatApprox(usageMonth.ai_credits, locale))}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {copy(
                  "opsEstimateInteractionsFriendly",
                  "Isso inclui cerca de {{n}} respostas automáticas no chat por mês (relatório + perguntas da equipe)."
                ).replace("{{n}}", formatApprox(usageMonth.interactions, locale))}
              </Typography>
              {aiPct != null && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={aiPct}
                    color={aiPct >= 90 ? "warning" : "primary"}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                  {planHeadroomPct != null ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {copy(
                        "opsEstimatePlanHeadroom",
                        "Restaria cerca de {{pct}}% do plano se só esta habilidade consumisse o limite."
                      ).replace("{{pct}}", String(planHeadroomPct))}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 0.25 }}>
              {copy(
                "opsEstimateDisclaimer",
                "É uma previsão. O uso real muda se o volume de atendimentos ou os formatos (áudio, vídeo) mudarem."
              )}
              {(estimate.notes || []).includes("veo_billed_via_gemini")
                ? ` ${copy("opsEstimateVideoNote", "Vídeos podem consumir mais do plano.")}`
                : ""}
            </Typography>
          </Stack>
        ) : !estimating ? (
          <Typography variant="body2" color="text.secondary">
            {copy("opsEstimateEmpty", "Ajuste os filtros para ver a estimativa.")}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {copy("opsTestTitle", "Testar na prática")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy(
              "opsTestLead",
              "Roda a skill de verdade com a configuração atual: a IA analisa os atendimentos, monta o briefing e envia aos destinatários (chat da equipe + canal escolhido)."
            )}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleTrigger}
              disabled={readOnly || !currentAgentId || triggering}
              startIcon={
                triggering ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
            >
              {triggering
                ? copy("opsTestRunning", "Testando…")
                : copy("opsTestNow", "Testar agora")}
            </Button>
            {!currentAgentId ? (
              <Typography variant="caption" color="text.secondary">
                {copy(
                  "opsTestSaveAgentFirst",
                  "Salve o agente antes de testar."
                )}
              </Typography>
            ) : null}
          </Stack>
          {triggering && testProgress ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {testProgress}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {okMsg && <Alert severity="success">{okMsg}</Alert>}

      {testRun && String(testRun.status) !== "failed" ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.light",
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {copy("opsTestResultTitle", "Resultado do teste")}
              </Typography>
              <Chip
                size="small"
                color={
                  ["delivered", "completed"].includes(String(testRun.status))
                    ? "success"
                    : "default"
                }
                label={`#${testRun.id} · ${testRun.status}`}
              />
              {testRun.period_start && testRun.period_end ? (
                <Typography variant="caption" color="text.secondary">
                  {formatOpsPeriodRange(testRun.period_start, testRun.period_end, " → ")}
                </Typography>
              ) : null}
            </Stack>

            {testAnalysis?.resumo ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {copy("opsTestResumoLabel", "Resumo da IA")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {String(testAnalysis.resumo)}
                </Typography>
              </Box>
            ) : null}

            {testBriefing && testBriefing !== testAnalysis?.resumo ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {copy("opsTestBriefingLabel", "Briefing enviado")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-wrap",
                    maxHeight: 220,
                    overflow: "auto",
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                  }}
                >
                  {String(testBriefing)}
                </Typography>
              </Box>
            ) : null}

            {testAssets.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {testAssets.map((asset, idx) =>
                  asset?.url ? (
                    <Button
                      key={`${asset.kind || "asset"}-${idx}`}
                      size="small"
                      component="a"
                      href={asset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon fontSize="inherit" />}
                    >
                      {asset.kind || copy("opsTestAsset", "Mídia")}
                    </Button>
                  ) : null
                )}
              </Stack>
            ) : null}

            {testMediaErrors.length > 0 ? (
              <Alert severity="warning">
                {copy(
                  "opsTestMediaErrors",
                  "Algumas mídias falharam na geração:"
                )}{" "}
                {testMediaErrors
                  .map((e) => `${e.step || "media"}: ${e.message || ""}`)
                  .join(" · ")}
              </Alert>
            ) : null}

            {testDelivery?.warning === "no_recipients" || testDelivery?.recipients === 0 ? (
              <Alert severity="error">
                {copy(
                  "opsTestNoRecipients",
                  "Nenhum destinatário resolvido — nada foi postado no chat nem enviado no canal."
                )}
              </Alert>
            ) : null}

            {testDmFailed ? (
              <Alert severity="error">
                {copy(
                  "opsTestDmFailed",
                  "Falha ao gravar no chat da equipe. Abra o detalhe do envio ou rode o teste de novo após o deploy."
                )}
                {testDeliveryRows
                  .filter((d) => d?.dm?.ok === false)
                  .map((d) => ` · ${d.dm?.error || ""}`)
                  .join("")}
              </Alert>
            ) : null}

            {testWaFailed ? (
              <Alert severity="error">
                {copy(
                  "opsTestWaFailed",
                  "Falha no envio WhatsApp (template/telefone/canal). O chat da equipe pode ter recebido o texto mesmo assim."
                )}
                {testDeliveryRows
                  .filter((d) => d?.channel?.ok === false)
                  .map((d) => ` · ${d.channel?.error || d.channel?.reason || ""}`)
                  .join("")}
              </Alert>
            ) : null}

            {testWaMediaNote && !testWaFailed ? (
              <Alert severity="info">
                {copy(
                  "opsTestWaMediaNote",
                  "WhatsApp oficial: o template leva o texto (agora com quebras de linha e links das mídias). Áudio/imagem/vídeo como arquivo só entram se a janela 24h estiver aberta — o pacote completo fica no chat da equipe."
                )}
              </Alert>
            ) : null}

            <Typography variant="caption" color="text.secondary">
              {copy(
                "opsTestDeliveryHint",
                "O relatório completo (texto + mídias) é postado no chat da equipe com o agente — não no simulador ao lado. Lá você pode fazer perguntas de follow-up."
              )}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                disabled={!currentAgentId || openingTeamChat}
                endIcon={<OpenInNewIcon fontSize="inherit" />}
                onClick={async () => {
                  if (!currentAgentId) return;
                  setOpeningTeamChat(true);
                  try {
                    // Reabre DM ocultada (membership soft-delete) antes de navegar.
                    const dm = await createTeamDm({ agentId: String(currentAgentId) });
                    const spaceId = Number(dm?.id || testSpaceId || 0);
                    if (spaceId > 0) {
                      navigate(`/equipe/${spaceId}`);
                    } else {
                      navigate("/equipe");
                    }
                  } catch (_) {
                    if (testSpaceId) navigate(`/equipe/${testSpaceId}`);
                    else navigate("/equipe");
                  } finally {
                    setOpeningTeamChat(false);
                  }
                }}
              >
                {openingTeamChat
                  ? copy("opsTestOpeningTeamChat", "Abrindo…")
                  : copy("opsTestOpenTeamChat", "Abrir chat da equipe")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {runs?.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {copy("opsRecentRuns", "Últimos envios")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            {copy("opsRecentRunsHint", "Clique em um envio para ver destinatários, canal e status de entrega.")}
          </Typography>
          <List dense disablePadding>
            {runs.map((r) => {
              const usage =
                r.usage_json && typeof r.usage_json === "object"
                  ? r.usage_json
                  : typeof r.usage_json === "string"
                    ? (() => {
                        try {
                          return JSON.parse(r.usage_json);
                        } catch {
                          return {};
                        }
                      })()
                    : {};
              const rec =
                r.recipients_json && typeof r.recipients_json === "object"
                  ? r.recipients_json
                  : {};
              const nRecipients = Array.isArray(rec.deliveries)
                ? rec.deliveries.length
                : Number(rec.recipients || 0);
              const waDlr = usage?.whatsapp_dlr;
              const waOut = usage?.whatsapp_outbound;
              let waLabel = "";
              if (waDlr?.failed) {
                waLabel = ` · WA ${copy("opsStatusError", "erro")}`;
              } else if (waDlr && waDlr.failed === false) {
                waLabel = Number(waDlr.statusCode) === 3
                  ? ` · WA ${copy("opsStatusRead", "lida")}`
                  : ` · WA ${copy("opsStatusDelivered", "entregue")}`;
              } else if (waOut?.pending_dlr) {
                waLabel = ` · WA ${copy("opsStatusPending", "aguardando")}`;
              } else if (waOut?.ok === false) {
                waLabel = ` · WA ${copy("opsStatusError", "erro")}`;
              } else if (r.error_message) {
                waLabel = ` · ${String(r.error_message).slice(0, 80)}`;
              }
              return (
                <ListItem key={r.id} disablePadding>
                  <ListItemButton onClick={() => openRunDetail(r)} sx={{ borderRadius: 1, py: 0.5 }}>
                    <ListItemText
                      primary={`#${r.id} · ${r.status}${nRecipients ? ` · ${nRecipients} dest.` : ""}`}
                      secondary={`${formatOpsPeriodRange(r.period_start, r.period_end) || "—"} · ${formatDateTimePtBR(r.created_at)}${waLabel}`}
                      primaryTypographyProps={{ variant: "body2" }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedRunId)}
        onClose={() => {
          setSelectedRunId(null);
          setRunDetail(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {copy("opsRunDetailTitle", "Detalhe do envio")}
          {selectedRunId ? ` #${selectedRunId}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {runDetailLoading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {copy("opsRunDetailPeriod", "Período")}:{" "}
                {formatOpsPeriodRange(runDetail?.period_start, runDetail?.period_end) || "—"}
                {" · "}
                {formatDateTimePtBR(runDetail?.created_at)}
              </Typography>
              <Typography variant="body2">
                {copy("opsRunDetailStatusLabel", "Status do run")}: {runDetail?.status || "—"}
              </Typography>
              {(() => {
                const rec =
                  runDetail?.recipients_json && typeof runDetail.recipients_json === "object"
                    ? runDetail.recipients_json
                    : {};
                const deliveries = Array.isArray(rec.deliveries) ? rec.deliveries : [];
                const channel = rec.deliveryChannel || deliveries[0]?.deliveryChannel || "—";
                if (!deliveries.length) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {copy("opsNoRecipients", "Nenhum destinatário registrado neste envio.")}
                    </Typography>
                  );
                }
                const statusCopy = {
                  sent: copy("opsStatusSent", "enviada"),
                  delivered: copy("opsStatusDelivered", "entregue"),
                  read: copy("opsStatusRead", "lida"),
                  error: copy("opsStatusError", "erro"),
                  skipped: copy("opsStatusSkipped", "não enviada"),
                  recorded: copy("opsStatusRecorded", "gravada no chat"),
                  pending: copy("opsStatusPending", "aguardando"),
                };
                const channelCopy = {
                  whatsapp: copy("opsDeliveryWhatsapp", "WhatsApp"),
                  email: copy("opsDeliveryEmail", "E-mail"),
                  sms: copy("opsDeliverySms", "SMS"),
                  voice: copy("opsDeliveryVoice", "Telefone"),
                };
                return (
                  <>
                    <Typography variant="subtitle2">
                      {copy("opsRunDetailChannel", "Canal")}: {channelCopy[channel] || channel}
                    </Typography>
                    <List dense disablePadding>
                      {deliveries.map((d, idx) => {
                        const st = deliveryStatusKey(d);
                        const err =
                          d.channel?.dlr?.friendly ||
                          d.channel?.dlr?.errorReason ||
                          d.channel?.error ||
                          "";
                        return (
                          <ListItem key={`${d.usuarioId || idx}`} alignItems="flex-start" sx={{ px: 0 }}>
                            <ListItemText
                              primary={d.nome || d.email || `Usuário #${d.usuarioId || "?"}`}
                              secondary={
                                [
                                  `${copy("opsRunDetailChannel", "Canal")}: ${
                                    channelCopy[d.deliveryChannel || channel] || d.deliveryChannel || channel
                                  }`,
                                  `${copy("opsRunDetailStatus", "Status")}: ${statusCopy[st] || st}`,
                                  d.dm?.ok
                                    ? copy("opsRunDetailDmOk", "Chat da equipe: gravado")
                                    : d.dm?.error
                                      ? `${copy("opsRunDetailDm", "Chat da equipe")}: ${d.dm.error}`
                                      : null,
                                  err ? String(err).slice(0, 160) : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </>
                );
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedRunId(null);
              setRunDetail(null);
            }}
          >
            {copy("opsRunDetailClose", "Fechar")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
